// import node module libraries
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import { Link } from 'react-router-dom';
import { Col, Form, Image, Row, Table } from 'react-bootstrap';
import {
	useFilters,
	useGlobalFilter,
	usePagination,
	useTable
} from 'react-table';

// import custom components
import GlobalFilter from 'components/elements/advance-table/GlobalFilter';
import Pagination from 'components/elements/advance-table/Pagination';

// import utility file
import { Avatar } from '../../../elements/bootstrap/Avatar';
import MaterielService from '../../services/MaterielService';
import LocalService from '../../services/LocalService';
import EleveService from '../../services/EleveService';

const MaterielsListItems = () => {
	const [originalMateriels, setOriginalMateriels] = useState([]);
	const [materiels, setMateriels] = useState([]);
	const [local, setLocal] = useState([]); // Add this line
	const [localOptions, setLocalOptions] = useState([]);
	const [nom, setEleve] = useState([]); // Add this line
	const [nomOptions, setEleveOptions] = useState([]);

	useEffect(() => {
		const fetchLocalOptions = async () => {
			try {
				const locals = await LocalService.getLocalOptions();
				setLocalOptions(locals);
			} catch (error) {
				console.error('Error fetching class options', error);
			}
		};

		fetchLocalOptions();
	}, []);

	const LOCAL_CHOICES = localOptions;

	useEffect(() => {
		Promise.all([
			MaterielService.getAllMateriels(),
			LocalService.getAllLocals()
		]).then(([materielsData, localData]) => {
			setOriginalMateriels(materielsData);
			setMateriels(materielsData);
			setLocal(localData);
		});
	}, []);

	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<Link
			to=""
			ref={ref}
			onClick={(e) => {
				e.preventDefault();
				onClick(e);
			}}
			className="btn-icon btn btn-ghost btn-sm rounded-circle"
		>
			{children}
		</Link>
	));

	const formatValue = (value, columnId) => {
		if (typeof value === 'string') {
			if (columnId === 'nom' && value) {
				return value.toUpperCase();
			} else if (
				columnId === 'local' ||
				(columnId === 'local' && value)
			) {
				return (
					value && value.charAt(0).toUpperCase() + value.slice(1).toLowerCase()
				);
			} else {
				return value;
			}
		} else if (columnId === 'local') {
			return value && value.nom
				? value.nom.toUpperCase()
				: ''; // Return the nom property
		}
		return value;
	};

	const formatStatutMateriel = (value) => {
		// Remplacez les underscores par des espaces et mettez la première lettre en majuscule
		const formattedValue = value
			.replace(/_/g, ' ')
			.replace(/\b\w/g, (c) => c.toUpperCase());
		return formattedValue;
	};

	const getLocalById = (localId) => {
		return local.find((item) => item.id === localId) ?? {};
	};

	const columns = useMemo(
		() => [
			{ accessor: 'id', Header: 'ID', show: false },
			{
				accessor: 'nom',
				Header: 'Nom',
				Cell: ({ value, row }) => {
					return (
						<div className="d-flex align-items-center">
							{value ? (
								<Link to={`/assets/materiels/${row.original.id}`}>
									<h5 className="mb-0 ms-2">{formatValue(value, 'nom')}</h5>
								</Link>
							) : null}
						</div>
					);
				}
			},
			{
				accessor: 'fournisseur',
				Header: 'Fournisseur',
				Cell: ({ value }) => <div>{formatValue(value, 'fournisseur')}</div>
			},
			{
				accessor: 'date_achat',
				Header: "Date d'achat",
				Cell: ({ value }) => <div>{formatValue(value, 'date_achat')}</div>
			},
			{
				accessor: 'cout',
				Header: 'Cout',
				Cell: ({ value }) => <div>{formatStatutMateriel(value)}</div>
			},
			{
				accessor: 'local',
				Header: 'Local',
				Cell: ({ value }) => <div>{formatValue(value, 'local')}</div>
			}
		],
		[]
	);

	const data = useMemo(() => {
		return materiels.map((materiel) => ({
			...materiel,
			local: getLocalById(materiel.local),
		}));
	}, [materiels]);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		page,
		nextPage,
		previousPage,
		state,
		gotoPage,
		pageCount,
		prepareRow,
		setGlobalFilter
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
				hiddenColumns: columns.map((column) => {
					if (column.show === false) return column.accessor || column.id;
					else return false;
				})
			}
		},
		useFilters,
		useGlobalFilter,
		usePagination
	);

	const { pageIndex, globalFilter } = state;

	const getFilterTerm = (event) => {
		let filterTerm = event.target.value;
		if (filterTerm !== '') {
			const newMaterielsList = originalMateriels.filter((materiel) => {
				return materiel.local === Number(filterTerm);
			});
			setMateriels(newMaterielsList);
		} else {
			setMateriels(originalMateriels);
		}
	};

	return (
		<Fragment>
			<div className="overflow-hidden p-3">
				<Row className="justify-content-md-between mb-4 mb-xl-0 ">
					<Col xl={2} lg={4} md={6} xs={12}>
						<GlobalFilter
							filter={globalFilter}
							setFilter={setGlobalFilter}
							placeholder="Rechercher un élève"
						/>
					</Col>
					<Col xxl={2} lg={2} md={6} xs={12}>
						{/* records filtering options */}
						<Form.Control
							as={FormSelect}
							placeholder="Filtrer par salle de local"
							options={LOCAL_CHOICES}
							onChange={getFilterTerm}
						/>
					</Col>
				</Row>
			</div>

			<Table
				hover
				responsive
				{...getTableProps()}
				className="text-nowrap table-centered"
			>
				<thead className="table-light">
					{headerGroups.map((headerGroup) => (
						<tr {...headerGroup.getHeaderGroupProps()}>
							{headerGroup.headers.map((column) => (
								<th {...column.getHeaderProps()}>{column.render('Header')}</th>
							))}
						</tr>
					))}
				</thead>
				<tbody {...getTableBodyProps()}>
					{page.map((row) => {
						prepareRow(row);
						return (
							<tr {...row.getRowProps()}>
								{row.cells.map((cell) => {
									return (
										<td {...cell.getCellProps()}>{cell.render('Cell')}</td>
									);
								})}
							</tr>
						);
					})}
				</tbody>
			</Table>

			<Pagination
				previousPage={previousPage}
				pageCount={pageCount}
				pageIndex={pageIndex}
				gotoPage={gotoPage}
				nextPage={nextPage}
			/>
		</Fragment>
	);
};

export default MaterielsListItems;
